import { actionHint } from '@/ui/helpers/scoreTriggerHelper';
import { defineComponent, h, ref, computed, onUnmounted, onMounted } from 'vue';
import { button, col, cols, Tag } from '@/ui/components';
import { Icon } from '@/ui/components/icon/Icon';
import { issueTemplateCreateModal } from '@/ui/components/issueTemplate/issueCreateTemplateModal/IssueTemplateCreateModal';
import { IssueTemplateEditable } from '@/ui/DTO';
import {
  actions,
  addAction,
  removeAction,
  undeletedActions,
  updateAction
} from '@/ui/pages/checklist/ChecklistBuilderIssueTemplateStore';
import { useCurrentRoute } from '@/ui/routes';
import { useTask } from '@/ui/concerns';
import { intoEditable, list } from '@/services/api/resources/issueTemplate';
import { t } from '@/ui/i18n/i18n';
import { FeatureNames, newFeature } from '@/ui/components/NewFeature';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { Tooltip } from '@/ui/components/Tooltip';

export const Actions = defineComponent({
  name: 'Actions',
  setup() {
    const currentRoute = useCurrentRoute();

    const isEditing = computed(() => !!currentRoute.value.params.id);

    const isModalOpen = ref(false);

    const selectedActionEdit = ref<IssueTemplateEditable | null>(null);
    const isEditingAction = computed(() => selectedActionEdit.value !== null);

    const toggleModal = (value: boolean) => {
      return () => {
        EventBus.$emit(Events.viewedNewFeature, FeatureNames.ADD_ACTION_TO_CHECKLIST);

        isModalOpen.value = value;
        selectedActionEdit.value = null;
      };
    };

    const onSave = (issueTemplate: IssueTemplateEditable) => {
      if (isEditingAction.value) {
        updateAction(issueTemplate);
      } else {
        addAction(issueTemplate);
      }

      isModalOpen.value = false;
    };

    const onActionRemove = (issueTemplate: IssueTemplateEditable) => removeAction(issueTemplate);

    const onEditClick = (resource: IssueTemplateEditable) => {
      selectedActionEdit.value = resource;
      isModalOpen.value = true;
    };

    const resetActions = () => actions.value = [];

    const issueTemplateListQuery = computed(() => ({
      page: 1,
      perPage: 50,
      checklist: currentRoute.value.params.id ? [currentRoute.value.params.id as unknown as number] : [],
    }));

    const getIssueTemplates = useTask(
      () => list(issueTemplateListQuery.value),
      response => {
        actions.value = response.data.map(intoEditable);
      }
    );

    const fetchIssueTemplates = () => {
      if (isEditing.value) {
        getIssueTemplates.run();
      }
    };

    onUnmounted(resetActions);
    onMounted(fetchIssueTemplates);

    return function render() {
      return h('div', [
        issueTemplateCreateModal({
          props: {
            active: isModalOpen.value,
            issueTemplate: selectedActionEdit.value as IssueTemplateEditable,
          },
          on: { close: toggleModal(false), save: onSave }
        }),
        newFeature({ featureName: FeatureNames.ADD_ACTION_TO_CHECKLIST, classes: 'mb-4' }),
        h('div', { class: 'checklist-builder__second-stage-title' }, [
          t('issue-template.issue'),
          h(
            Tooltip,
            {
              props: {
                label: t('issue-template.tooltip'),
                position: 'is-top',
              },
            },
            [h(
              Tag,
              { class: 'tag--left-margin', props: { type: 'success' } },
              [
                t('new-features.new'),
                h(Icon, {
                  props: {
                    size: 'xs',
                    iconPack: 'fal',
                    icon: 'info-circle',
                  },
                }),
              ],
            )],
          ),
        ]),
        h('div', { class: 'text-sm text-70 mb-4' }, t('issue-template.description')),
        undeletedActions.value.map(action => cols([
          col([
            h('div', action.title),
            action.scoreTrigger && h('div', { class: 'text-sm text-70 mt-1' }, actionHint(action.scoreTrigger)),
          ]),
          col([
            h(Icon, {
              class: 'mr-2 cursor-pointer has-text-grey',
              props: {
                icon: 'edit',
                size: '1.5x',
              },
              on: {
                click: () => onEditClick(action),
              }
            }),
            h(Icon, {
              class: 'cursor-pointer has-text-grey',
              props: {
                icon: 'trash-alt',
                size: '1.5x',
              },
              on: {
                click: () => onActionRemove(action)
              }
            }),
          ], true)
        ])),
        button(
          undeletedActions.value.length > 0
            ? t('issue-template.add-another-action')
            : t('issue-template.add-action'),
          toggleModal(true),
          undefined,
          { outlined: true }
        ),
      ]);
    };
  },
});
